import React, { useState, useEffect, useRef } from 'react';
import Header from './common/Header';
import Footer from './common/Footer';
import LoginPopup from './popup/LoginPopup';
import ReviewModal from './popup/ReviewModal';
import SEO from '../components/common/SEO';
import './HolidaysListing.css';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getValidSession } from './helpers/sessionUtils';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { buildVendorProfileUrl } from '../utils/vendorProfileUrl';
import ListingVerifiedBadge from '../components/common/ListingVerifiedBadge';
import VendorProfileButton from '../components/common/VendorProfileButton';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ["places"];

// --- STAR RATING COMPONENT ---
const StarRating = ({ avg = 0, count = 0, onClick }) => {
    const fullStars = Math.floor(avg);
    const hasHalf = avg - fullStars >= 0.5;

    return (
        <div className="d-flex align-items-center justify-content-center gap-2" style={{ cursor: 'pointer' }} onClick={onClick}>
            <div className="d-flex" style={{ gap: "2px" }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <i
                        key={star}
                        className={`bi ${star <= fullStars ? "bi-star-fill" : star === fullStars + 1 && hasHalf ? "bi-star-half" : "bi-star"} text-warning`}
                        style={{ fontSize: "16px" }}
                    />
                ))}
            </div>
            <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                {Math.round(avg)}/5
            </span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
                ({count} reviews)
            </span>
        </div>
    );
};

// --- HOLIDAY CARD COMPONENT ---
const HolidayCard = ({ pkg, onAction, onReviewClick, onCardClick, isSubmitting, pendingAction, activeId }) => {
    const isActive = activeId === pkg.id && isSubmitting;
    const displayImage = pkg.image || '/assets/img/tours-details/arunachal-package.jpg';
    const vendorId = pkg.Vendor?.id || pkg.vendor_id || pkg.user_id;
    const vendorProfileUrl = buildVendorProfileUrl(pkg.Vendor, vendorId);

    // Calculate average rating
    const avgRating = pkg.avg_rating || 0;
    const reviewCount = pkg.review_count || 0;

    return (
        <div
            className="card mb-2 shadow-sm rounded-2 bg-white overflow-hidden result-card-hover"
            style={{
                cursor: 'default',
                border: '1px solid #e0e0e0'
            }}
        >
            <div className="row g-0 align-items-stretch">
                {/* IMAGE */}
                <div className="col-12 col-md-4 col-lg-3 p-0 d-flex align-items-stretch justify-content-stretch bg-light-subtle">
                    <div
                        className="overflow-hidden shadow-sm w-100 position-relative"
                        style={{
                            borderColor: '#ddd'
                        }}
                    >
                        <img
                            src={displayImage}
                            alt={pkg.package_name}
                            className="w-100 h-100"
                            style={{ objectFit: "cover", display: "block", cursor: "pointer" }}
                            onClick={() => onCardClick(pkg)}
                            onError={(e) => {
                                e.target.src = '/assets/img/tours-details/arunachal-package.jpg';
                            }}
                        />
                        <ListingVerifiedBadge item={pkg} />
                        {/* VERIFIED BADGE - TOP LEFT */}
                        {/* DURATION BADGE - TOP RIGHT */}
                        <div
                            className="position-absolute badge bg-primary"
                            style={{
                                top: '12px',
                                right: '12px',
                                padding: '8px 12px',
                                borderRadius: '6px'
                            }}
                        >
                            {pkg.nights ? `${pkg.nights}N/${pkg.days}D` : (pkg.days ? `${pkg.days}D` : '')}
                        </div>
                    </div>
                </div>

                {/* INFO */}
                <div className="col-12 col-md-8 col-lg-6 p-3 text-start">
                    <div>
                        {/* Title */}
                        <h4
                            className="mb-3"
                            style={{ color: '#333', fontSize: '18px', cursor: 'pointer' }}
                            onClick={() => onCardClick(pkg)}
                        >
                            {pkg.package_name}
                        </h4>

                        {/* Location & Duration Tags */}
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                            <div className="d-inline-flex align-items-center gap-1">
                                <span className="badge bg-light text-dark" style={{ fontSize: '12px' }}>
                                    <i className="bi bi-geo-alt-fill me-1" style={{ color: '#dc3545' }}></i> {pkg.city}
                                </span>

                            </div>
                            <span className="badge bg-light text-dark" style={{ fontSize: '12px' }}>
                                <i className="bi bi-calendar-event me-1" style={{ color: '#0dcaf0' }}></i> {pkg.nights ? `${pkg.nights}N/${pkg.days}D` : (pkg.days ? `${pkg.days} Days` : 'Duration N/A')}
                            </span>
                            {vendorProfileUrl && (
                                <VendorProfileButton to={vendorProfileUrl} />
                            )}
                        </div>

                        {/* Description */}
                        <p className="mb-3 holiday-card-description" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                            {pkg.description?.length > 120 ? `${pkg.description.substring(0, 120)}...` : pkg.description}
                        </p>

                        {/* Starting From Price */}
                        <div className="mb-0">
                            <p className="mb-1 text-muted" style={{ fontSize: '12px' }}>Starting from</p>
                            <h5 className="fw-bold mb-0" style={{ color: '#0095D3', fontSize: '18px' }}>
                                ₹{pkg.package_price ? Math.round(pkg.package_price) : 'Price on request'} <span style={{ fontSize: '12px', fontWeight: '500', color: '#666' }}>/per person</span>
                            </h5>
                        </div>
                    </div>
                </div>

                {/* BUTTONS */}
                <div className="col-12 col-lg-3 p-3 p-lg-4 d-flex flex-column gap-2 border-start holiday-card-actions">
                    <div className="d-none d-lg-block mb-lg-3 pb-lg-3 border-bottom text-center">
                        <StarRating avg={avgRating} count={reviewCount} onClick={() => onReviewClick(pkg)} />
                    </div>

                    <div className="d-none d-md-block d-lg-none mb-2 pb-2 border-bottom text-center">
                        <StarRating avg={avgRating} count={reviewCount} onClick={() => onReviewClick(pkg)} />
                    </div>

                    {/* Mobile: review summary left, call/WhatsApp icons right */}
                    <div className="holiday-mobile-action-row d-md-none">
                        <div className="holiday-mobile-rating">
                            <StarRating avg={avgRating} count={reviewCount} onClick={() => onReviewClick(pkg)} />
                        </div>
                        <div className="holiday-mobile-icons">
                            <button
                                className="holiday-mobile-icon-btn"
                                onClick={(e) => onAction(e, 'call', pkg)}
                                disabled={isSubmitting}
                                title="Call Now"
                            >
                                {isActive && pendingAction === 'call' ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <img src="/assets/img/mobile-icon/call.png" alt="Call" />
                                )}
                            </button>
                            <button
                                className="holiday-mobile-icon-btn"
                                onClick={(e) => onAction(e, 'whatsapp', pkg)}
                                disabled={isSubmitting}
                                title="Contact on WhatsApp"
                            >
                                {isActive && pendingAction === 'whatsapp' ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <img src="/assets/img/mobile-icon/whatsapp.png" alt="WhatsApp" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Tablet: compact icon-only buttons in a row */}
                    <div className="d-none d-md-flex d-lg-none gap-2 justify-content-center">
                        <button
                            className="btn btn-outline-primary fw-bold rounded-3 d-flex align-items-center justify-content-center"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'call', pkg)}
                            disabled={isSubmitting}
                            title="Call Now"
                        >
                            {isActive && pendingAction === 'call' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-telephone-fill"></i>
                            )}
                        </button>
                        <button
                            className="btn btn-outline-success fw-bold rounded-3 d-flex align-items-center justify-content-center"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'whatsapp', pkg)}
                            disabled={isSubmitting}
                            title="Contact Owner"
                        >
                            {isActive && pendingAction === 'whatsapp' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-whatsapp"></i>
                            )}
                        </button>
                        <button
                            className="btn btn-outline-dark fw-bold rounded-3 d-flex align-items-center justify-content-center listing-book-btn"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'book', pkg)}
                            disabled={isSubmitting}
                            title="Book Now"
                        >
                            {isActive && pendingAction === 'book' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-calendar-check-fill"></i>
                            )}
                        </button>
                    </div>

                    {/* Desktop: stacked full-width buttons */}
                    <div className="d-none d-lg-flex flex-column gap-2">
                        <button
                            className="btn btn-outline-primary fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, "call", pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === "call" ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-telephone-fill me-2"></i>
                            )}
                            {isActive && pendingAction === "call" ? "Connecting..." : "Call Now"}
                        </button>

                        <button
                            className="btn btn-outline-success fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, "whatsapp", pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === "whatsapp" ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-whatsapp me-2"></i>
                            )}
                            {isActive && pendingAction === "whatsapp" ? "Connecting..." : "WhatsApp Now"}
                        </button>

                        <button
                            className="btn btn-outline-dark fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100 listing-book-btn"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, "book", pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === "book" ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : null}
                            {isActive && pendingAction === "book" ? "Processing..." : "Book Now"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- PAGINATION COMPONENT ---
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= maxVisible; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    return (
        <nav aria-label="Page navigation" style={{ marginTop: '30px', marginBottom: '30px' }}>
            <ul className="pagination d-flex justify-content-center flex-wrap gap-2" style={{ listStyle: 'none', padding: 0 }}>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
                        style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: '#32AEE4' }}>
                        <i className="bi bi-chevron-left"></i> Previous
                    </button>
                </li>

                {getPageNumbers().map((page, idx) => (
                    <li key={idx} className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
                        style={{ pointerEvents: page === '...' ? 'none' : 'auto' }}>
                        <button
                            className="page-link"
                            onClick={() => typeof page === 'number' && onPageChange(page)}
                            disabled={page === '...'}
                            style={{
                                padding: '8px 12px',
                                border: page === currentPage ? '2px solid #32AEE4' : '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: page === currentPage ? '#32AEE4' : 'white',
                                color: page === currentPage ? 'white' : '#32AEE4',
                                cursor: page === '...' ? 'default' : 'pointer',
                                fontWeight: page === currentPage ? '600' : '400',
                                minWidth: '36px'
                            }}
                        >
                            {page}
                        </button>
                    </li>
                ))}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
                        style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: '#32AEE4' }}>
                        Next <i className="bi bi-chevron-right"></i>
                    </button>
                </li>
            </ul>
        </nav>
    );
};

function HolidaysListing() {
    const [packages, setPackages] = useState([]);
    const [visiblePackages, setVisiblePackages] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const packagesPerPage = 20;
    const location = useLocation();

    // Filter state
    const [selectedCity, setSelectedCity] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [durationMax, setDurationMax] = useState('Any');
    const [nameQuery, setNameQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [citySearchInput, setCitySearchInput] = useState('');

    // User & Lead state
    const [user, setUser] = useState(null);
    const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [activeEntity, setActiveEntity] = useState(null);
    const [showThanksModal, setShowThanksModal] = useState(false);

    // Review state
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAP_KEY,
        libraries,
    });

    const fromAutoRef = useRef(null);
    const loginSuccessRef = useRef(false);

    const getCityFromPlace = (place) => {
        if (!place) return '';
        const comp = place.address_components || [];
        const findType = (type) => {
            const c = comp.find((x) => x.types && x.types.includes(type));
            return c ? c.long_name : null;
        };
        return findType('locality') || findType('administrative_area_level_2') || place.name || '';
    };

    const handlePlaceChanged = () => {
        const inst = fromAutoRef.current;
        if (!inst) return;
        const place = inst.getPlace();
        const city = getCityFromPlace(place);
        if (city) {
            setSelectedCity(city);
            setCitySearchInput(city);
        }
    };

    // Load user from session
    useEffect(() => {
        const session = getValidSession();
        if (session?.user?.user || session?.user) {
            setUser(session.user?.user || session.user);
        }
    }, []);





    // If user arrived with a city query param, pre-select and auto-search
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const cityParam = params.get('city');
        const nameParam = params.get('name');
        if (cityParam) {
            setSelectedCity(cityParam);
            handleSearch(cityParam, sortBy, durationMax, nameParam || nameQuery);
        } else if (nameParam) {
            setNameQuery(nameParam);
            handleSearch('', sortBy, durationMax, nameParam);
        } else {
            handleSearch('', '', 'Any', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchFilteredPackages = async ({ city = '', sort = '', maxDays = 'Any', q = '' } = {}) => {
        setIsLoading(true);
        try {
            const params = {};
            if (city) params.city = city;
            if (q) params.q = q;
            if (maxDays && maxDays !== 'Any') params.maxDays = maxDays;
            if (sort) params.sort = sort;

            const res = await axios.get(`${API_BASE_URL}/holiday-packages/search-packages-listing`, { params });
            const pkgs = Array.isArray(res.data?.packages) ? res.data.packages : [];
            setPackages(pkgs);
            setCurrentPage(1);
            setVisiblePackages(pkgs.slice(0, packagesPerPage));
        } catch (err) {
            console.error('Failed to fetch packages', err);
            setPackages([]);
            setVisiblePackages([]);
            setCurrentPage(1);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (pageNumber) => {
        const startIndex = (pageNumber - 1) * packagesPerPage;
        const endIndex = startIndex + packagesPerPage;
        setVisiblePackages(packages.slice(startIndex, endIndex));
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearch = (city = selectedCity, sort = sortBy, maxDays = durationMax, q = nameQuery) => {
        setSelectedCity(city);
        setSortBy(sort);
        setDurationMax(maxDays);
        setNameQuery(q);
        setCitySearchInput(city);
        fetchFilteredPackages({ city, sort, maxDays, q });
    };



    const generateHolidaySlug = (pkg) => {
        const slugify = (str) => str ? str.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-').replace(/^-+/, '').replace(/-+$/, '') : '';
        const parts = [
            slugify(pkg.package_name || pkg.name),
            slugify(pkg.city),
            Math.round(pkg.offer_price || pkg.package_price || pkg.price || 0),
            pkg.id
        ].filter(Boolean).join('-');
        return parts.replace(/--+/g, '-');
    };

    // Handle card click for details page
    const handleCardDetailsClick = (pkg) => {
        window.open(`/holiday-details/${generateHolidaySlug(pkg)}`, '_blank', 'noopener,noreferrer');
    };

    // --- LEAD HANDLING ---
    const handleAction = (e, type, pkgData) => {
        e.preventDefault();
        e.stopPropagation();

        if (isSubmitting) return;

        setActiveEntity({ data: pkgData, type: 'holiday' });
        setPendingAction(type);

        if (!user) {
            setIsLoginPopupOpen(true);
            return;
        }

        processLead(type, user, { data: pkgData, type: 'holiday' });
    };

    const handleLoginSuccess = (loggedInUser) => {
        loginSuccessRef.current = true;
        const session = getValidSession();
        const resolvedUser = session?.user?.user || session?.user || loggedInUser;
        setUser(resolvedUser);
        setIsLoginPopupOpen(false);

        // If review modal was requested, open it
        if (selectedPackage) {
            setTimeout(() => {
                setShowReviewModal(true);
            }, 300);
        } else if (pendingAction && activeEntity) {
            processLead(pendingAction, resolvedUser, activeEntity);
        }
    };

    const handleLoginPopupClose = () => {
        setIsLoginPopupOpen(false);
        setIsSubmitting(false);
        setTimeout(() => {
            if (!loginSuccessRef.current && pendingAction && activeEntity) {
                setPendingAction(null);
                setActiveEntity(null);
            }
            loginSuccessRef.current = false;
        }, 0);
    };

    const processLead = async (actionType, userData, entity) => {
        setIsSubmitting(true);
        const currentUser = userData || user;
        const { data } = entity;

        const payload = {
            name: currentUser?.name || "Guest",
            email: currentUser?.email || "",
            mobile: (currentUser?.phone || currentUser?.mobile || "").replace(/^(\+91|91)/, ""),
            type: 'holiday', // The backend specifically expects 'holiday' here
            category: 'holidays',
            type_id: data.id,
            message: `User clicked ${actionType} for ${data.package_name}`
        };

        let success = false;
        try {
            const res = await axios.post(`${API_BASE_URL}/enquiries/create`, payload);
            if (res.data.success) {
                success = true;
                setShowThanksModal(true);
            } else {
                toast.error(res.data?.message || "Could not process request. Please try again.");
            }
        } catch (err) {
            toast.error("Error processing request. Please try again.");
            console.error(err);
        } finally {
            setIsSubmitting(false);
            if (!success) {
                setPendingAction(null);
            }
        }
    };

    const handleThankYouClose = () => {
        setShowThanksModal(false);
        setPendingAction(null);
        setActiveEntity(null);
    };

    const handleReviewClick = (pkg) => {
        // Check if user is logged in
        if (!user) {
            setSelectedPackage(pkg);
            setIsLoginPopupOpen(true);
            return;
        }

        setSelectedPackage(pkg);
        setShowReviewModal(true);
    };

    const durationOptions = [
        { value: 'Any', label: 'Select Duration' },
        ...Array.from({ length: 15 }, (_, i) => ({
            value: (i + 1).toString(),
            label: `${i + 1} Days`
        }))
    ];

    const sortOptions = [
        { value: '', label: 'Sorted By Price' },
        { value: 'low', label: 'Low to High' },
        { value: 'high', label: 'High to Low' }
    ];

    const customSelectStyles = {
        control: (base, state) => {
            const hasActiveValue = !['', 'Any'].includes(state.getValue()?.[0]?.value);
            return ({
                ...base,
                border: 'none',
                borderBottom: `1px solid ${hasActiveValue ? '#32AEE4' : '#8c8c8c'}`,
                borderRadius: 0,
                boxShadow: 'none',
                backgroundColor: 'transparent',
                padding: '0',
                minHeight: 'auto',
                fontFamily: "'Poppins'",
                fontWeight: '500',
                fontSize: '15px',
                color: 'black',
                cursor: 'pointer',
                textAlign: 'left',
            });
        },
        valueContainer: (base) => ({
            ...base,
            padding: '8px',
            paddingLeft: '0',
            textAlign: 'left',
        }),
        singleValue: (base, state) => ({
            ...base,
            color: ['', 'Any'].includes(state.data?.value) ? '#6c757d' : '#111827',
            fontWeight: 500,
            margin: '0',
            textAlign: 'left',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#6c757d',
            margin: '0',
        }),
        input: (base) => ({
            ...base,
            margin: '0',
            padding: '0',
            color: 'black',
            textAlign: 'left',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: '#32AEE4',
            padding: '8px',
        }),
        menu: (base) => ({
            ...base,
            fontFamily: "'Poppins'",
            fontSize: '15px',
            fontWeight: '500',
            zIndex: 9999,
            marginTop: '2px',
            textAlign: 'left',
        }),
        // eslint-disable-next-line no-unused-vars
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#32AEE4' : state.isFocused ? '#e2f2ff' : 'white',
            color: state.isSelected ? 'white' : 'black',
            cursor: 'pointer',
            padding: '10px 12px',
            textAlign: 'left',
        })
    };

    return (
        <div>
            <SEO title={selectedCity ? `Holiday Packages in ${selectedCity} | Mangoloops` : "Exclusive Holiday Packages | Mangoloops"} description="Discover breathtaking holiday packages with Mangoloops. From serene escapes to adventurous tours, find your perfect getaway today." />
            <Header />
            <LoginPopup isOpen={isLoginPopupOpen} onClose={handleLoginPopupClose} onLoginSuccess={handleLoginSuccess} />
            <main id="content">


                <div className="banner-content tour-search-sec text-center mb-5 holiday-listing-filter-section">
                    <div className="container-fluid container-package">
                        <div className="row justify-content-center py-xl-4">
                            <div className="card border-0 border-radius-12 tab-shadow holiday-listing-filter-card">
                                <div className="card-body padding-14">
                                    <form className="filter-form holiday-listing-filter-form p-0" onSubmit={(e) => { e.preventDefault(); handleSearch(citySearchInput || selectedCity || '', sortBy, durationMax, nameQuery); }}>
                                        <div className="form-group holiday-filter-field" style={{ minWidth: 200, position: 'relative', textAlign: 'left' }}>
                                            <div style={{ position: 'relative' }}>
                                                {isLoaded ? (
                                                    <Autocomplete
                                                        onLoad={(inst) => {
                                                            fromAutoRef.current = inst;
                                                            try {
                                                                inst.setFields(["address_components", "name", "formatted_address"]);
                                                            } catch (e) { }
                                                        }}
                                                        onPlaceChanged={handlePlaceChanged}
                                                        options={{
                                                            types: ["(cities)"]
                                                        }}
                                                    >
                                                        <input
                                                            type="text"
                                                            id="from-city"
                                                            className={`input-holiday ${citySearchInput ? 'filter-value-active' : ''}`}
                                                            placeholder="From City"
                                                            value={citySearchInput}
                                                            onChange={(e) => {
                                                                setCitySearchInput(e.target.value);
                                                                if (e.target.value === '') {
                                                                    setSelectedCity('');
                                                                }
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px',
                                                                border: 'none',
                                                                borderBottom: '1px solid #8c8c8c',
                                                                fontFamily: 'Poppins',
                                                                fontSize: '15px',
                                                                fontWeight: '500'
                                                            }}
                                                        />
                                                    </Autocomplete>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        id="from-city"
                                                        className={`input-holiday ${citySearchInput ? 'filter-value-active' : ''}`}
                                                        placeholder="All Cities"
                                                        value={citySearchInput}
                                                        onChange={(e) => {
                                                            setCitySearchInput(e.target.value);
                                                            if (e.target.value === '') {
                                                                setSelectedCity('');
                                                            }
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px',
                                                            border: 'none',
                                                            borderBottom: '1px solid #8c8c8c',
                                                            fontFamily: 'Poppins',
                                                            fontSize: '15px',
                                                            fontWeight: '500'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="sort-duration-wrapper">
                                            <div className="form-group form-border-left holiday-filter-field">
                                                <Select
                                                    inputId="sorted-by"
                                                    name="sorted-by"
                                                    value={sortOptions.find(o => o.value === sortBy) || sortOptions[0]}
                                                    onChange={(selectedOption) => setSortBy(selectedOption ? selectedOption.value : '')}
                                                    options={sortOptions}
                                                    styles={customSelectStyles}
                                                    isSearchable={false}
                                                    maxMenuHeight={180}
                                                />
                                            </div>
                                            <div className="form-group form-border-left holiday-filter-field" style={{ minWidth: 150, textAlign: 'left' }}>
                                                <Select
                                                    id="duration"
                                                    name="duration"
                                                    value={durationOptions.find(o => o.value.toString() === durationMax.toString()) || durationOptions[0]}
                                                    onChange={(selectedOption) => setDurationMax(selectedOption ? selectedOption.value : 'Any')}
                                                    options={durationOptions}
                                                    styles={customSelectStyles}
                                                    isSearchable={true}
                                                    placeholder="Any"
                                                    maxMenuHeight={220}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group form-border-left holiday-filter-field" style={{ minWidth: 220 }}>
                                            <input className={`input-holiday ${nameQuery ? 'filter-value-active' : ''}`}
                                                id="package-name"
                                                type="text"
                                                placeholder="Package name"
                                                value={nameQuery}
                                                onChange={(e) => setNameQuery(e.target.value)}
                                            />
                                        </div>
                                        <button type="submit" className="submit-btn-filter">Search</button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mb-7">
                    <div className="container">
                        <div className="row">
                            <div className="col-12 d-flex justify-content-between align-items-center mb-3 flex-wrap">
                                <h3 className="tour-title-of-trip holidays-left mb-0 heading-div"> Holiday Tour Packages</h3>
                                <div className="text-muted" style={{ fontSize: '14px' }}>
                                    Showing {visiblePackages.length} results of {packages.length}
                                </div>
                            </div>
                        </div>

                        <div className="row mt-2">
                            {isLoading && <div className="col-12 text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>}
                            {!isLoading && visiblePackages.length === 0 && (
                                <div className="col-12">
                                    <div className="alert alert-info text-center">No packages found for the selected filters.</div>
                                </div>
                            )}
                            {!isLoading && visiblePackages.map((pkg) => (
                                <div className="col-12 mb-2" key={pkg.id}>
                                    <HolidayCard
                                        pkg={pkg}
                                        onAction={handleAction}
                                        onReviewClick={handleReviewClick}
                                        onCardClick={handleCardDetailsClick}
                                        isSubmitting={isSubmitting}
                                        pendingAction={pendingAction}
                                        activeId={activeEntity?.data?.id}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* PAGINATION */}
                        {packages.length > packagesPerPage && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(packages.length / packagesPerPage)}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </div>
                </div>
            </main>
            <Footer />

            {/* --- THANK YOU MODAL --- */}
            {(isSubmitting || showThanksModal) && (
                <div className="custom-modal-overlay" style={{ zIndex: 9999999 }}>
                    <div className="thanks-modal card shadow-lg border-0 rounded-4 text-center p-5" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        {isSubmitting && !showThanksModal ? (
                            <>
                                <div className="mb-4">
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                                        <span className="visually-hidden">Processing...</span>
                                    </div>
                                </div>
                                <h4 className="fw-bold mb-2">Processing your request...</h4>
                                <p className="text-muted mb-0">Please wait while we save your lead and notify the vendor.</p>
                            </>
                        ) : (
                            <>
                                <div className="mb-4">
                                    <div className="success-checkmark-wrap">
                                        <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                                    </div>
                                </div>
                                <h2 className="fw-bold mb-3">Thank You!</h2>
                                <p className="text-muted mb-4">Your request has been sent to the vendor. We will get back to you shortly.</p>
                                <button className="btn btn-primary rounded-pill px-4 py-2 fw-bold" onClick={handleThankYouClose}>
                                    OK
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* --- REVIEW MODAL --- */}
            {showReviewModal && selectedPackage && (
                <ReviewModal
                    isOpen={showReviewModal}
                    onClose={() => {
                        setShowReviewModal(false);
                        setSelectedPackage(null);
                    }}
                    entity={selectedPackage}
                    entityType="holiday_package"
                    user={user}
                    apiEndpoint="/holiday-packages"
                />
            )}
        </div>
    );
}

export default HolidaysListing;
